import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  HelpCircle,
  MessageCircle,
  Zap,
  ShieldCheck,
  TrendingUp,
  CreditCard,
  User,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo64.png";

const FAQItem = ({ question, answer, isOpen, onClick }: any) => {
  return (
    <div className="border-b border-white/5 last:border-0">
      <button
        onClick={onClick}
        className="w-full py-6 flex items-center justify-between text-left group"
      >
        <span className="text-lg font-bold text-gray-200 group-hover:text-white transition-colors">
          {question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          className={`p-2 rounded-lg bg-white/5 text-gray-400 group-hover:text-accent-primary transition-colors`}
        >
          <ChevronDown size={20} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="pb-6 text-gray-400 leading-relaxed max-w-3xl">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const HelpPage: React.FC = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      category: "General",
      icon: HelpCircle,
      items: [
        {
          q: "What exactly is Vytta?",
          a: "Vytta is a high-precision stock signal platform. We provide real-time market calls (Entry, Target, and Stop-Loss) based on deep technical analysis and market sentiment to help you trade with confidence.",
        },
        {
          q: "Are the signals automated?",
          a: "Our signals are a hybrid of AI-driven data screening and expert human validation. This ensures speed without sacrificing the nuance of human market experience.",
        },
      ],
    },
    {
      category: "Trading & Signals",
      icon: TrendingUp,
      items: [
        {
          q: "How many signals do I get per day?",
          a: "Depending on your plan, Starter users receive 1-2 daily calls, while Pro and Institutional users get real-time intra-day alerts as opportunities emerge.",
        },
        {
          q: "What is the success rate of Vytta calls?",
          a: "While past performance doesn't guarantee future results, we maintain a transparent track record in the 'Performance' section of your dashboard. We prioritize risk-to-reward ratios of at least 1:2.",
        },
      ],
    },
    {
      category: "Billing & Security",
      icon: ShieldCheck,
      items: [
        {
          q: "Can I cancel my subscription anytime?",
          a: "Yes. You can manage or cancel your subscription directly from the Settings page. You will retain access until the end of your current billing cycle.",
        },
        {
          q: "Is my payment data secure?",
          a: "Absolutely. We use industry-standard encryption and processed via secure gateways. Vytta never stores your raw credit card information.",
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#08090a] text-white antialiased relative overflow-hidden">
      {/* Shared Background Effects */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,#1a1b23_0%,transparent_50%)] pointer-events-none" />
      <div className="fixed top-[-5%] right-[-5%] w-[30%] h-[30%] bg-accent-primary/5 rounded-full blur-[100px] pointer-events-none" />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-2xl bg-black/40 border-b border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div 
            onClick={() => navigate('/welcome')}
            className="flex items-center gap-3 group cursor-pointer"
          >
            <div className="relative w-8 h-8">
              <img src={logo} alt="V" className="relative w-full h-full object-cover rounded-lg border border-white/20" />
            </div>
            <span className="text-xl font-black italic bg-gradient-to-r from-white to-orange-500 bg-clip-text text-transparent">
              Vytta Help
            </span>
          </div>
          <button 
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white flex items-center gap-2 text-sm font-medium transition-colors"
          >
            <ArrowLeft size={16} /> Back
          </button>
        </div>
      </nav>

      {/* Hero Header */}
      <header className="max-w-4xl mx-auto px-6 pt-16 pb-12 text-center relative z-10">
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-black mb-6 bg-gradient-to-r from-white via-gray-200 to-orange-500 bg-clip-text text-transparent"
        >
          How can we help?
        </motion.h1>
        
        {/* Search Bar */}
        <div className="relative max-w-2xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
          <input 
            type="text"
            placeholder="Search for topics, features, or billing..."
            className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 focus:outline-none focus:border-accent-primary/50 focus:bg-white/10 transition-all backdrop-blur-xl"
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 pb-24 relative z-10">
        <div className="space-y-12">
          {faqs.map((section, sIdx) => (
            <motion.section 
              key={section.category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: sIdx * 0.1 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-xl bg-accent-primary/10 border border-accent-primary/20">
                  <section.icon size={20} className="text-accent-primary" />
                </div>
                <h2 className="text-xl font-black uppercase tracking-widest text-gray-400 text-sm">
                  {section.category}
                </h2>
              </div>

              <div className="bg-gradient-to-br from-dark-surface/80 to-dark-card/60 border border-white/10 rounded-3xl px-8 backdrop-blur-xl">
                {section.items.map((item, iIdx) => {
                  const globalIdx = sIdx * 10 + iIdx; // Unique key for state
                  return (
                    <FAQItem 
                      key={iIdx}
                      question={item.q}
                      answer={item.a}
                      isOpen={openIndex === globalIdx}
                      onClick={() => setOpenIndex(openIndex === globalIdx ? null : globalIdx)}
                    />
                  );
                })}
              </div>
            </motion.section>
          ))}
        </div>

        {/* Support CTA */}
        <motion.div 
          whileHover={{ scale: 1.01 }}
          className="mt-16 p-8 bg-gradient-to-r from-accent-primary/20 to-accent-secondary/10 border border-accent-primary/20 rounded-3xl flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-xl">
              <MessageCircle size={28} className="text-accent-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Still have questions?</h3>
              <p className="text-gray-400">Our support team is available 24/7 for Gold Plan members.</p>
            </div>
          </div>
          <a 
            href="https://wa.me/9051742762"
            target="_blank"
            className="bg-white text-black px-8 py-4 rounded-full font-black text-sm hover:bg-orange-500 hover:text-white transition-all shadow-xl"
          >
            Contact Support
          </a>
        </motion.div>
      </main>

      {/* Footer (Simplified) */}
      <footer className="border-t border-white/5 py-12 text-center text-gray-500 text-sm">
        <p>© {new Date().getFullYear()} Vytta Fintech. Trade Responsibly.</p>
      </footer>
    </div>
  );
};

export default HelpPage;